// B2 login page (T04 §9.5): email + password through Better Auth. Labels are the Playwright locators
// (getByLabel("Email") / getByLabel("Password") / getByRole("button", { name: "Sign in" })).
import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@vbltc/ui/button";
import { Input } from "@vbltc/ui/input";
import { StatusRegion } from "@vbltc/ui/components/status-region/status-region";
import { AUTH_BUTTON_CLASS, AUTH_INPUT_CLASS, AUTH_LABEL_CLASS, AUTH_LINK_CLASS } from "@vbltc/ui";
import { authClient } from "../features/auth/client";
import { formString } from "../features/auth/form-value";
import { DEFAULT_IDLE_MINUTES } from "../features/auth/idle-timing";
import { clearUserState } from "../features/auth/clear-user-state";
import { loadAuthOptions } from "../features/auth/api/auth-options";
import { returnTarget, signedInUserId } from "../features/auth/return-to";
import { isServerPage } from "../features/auth/session-read";
import { log } from "../telemetry";
import { AuthPage } from "../app/auth-page";
import { loginSearchSchema } from "@/lib/route-search";

export const Route = createFileRoute("/login")({
  validateSearch: loginSearchSchema,
  // J-257: whether this server can mail a reset link, known before the page draws.
  loader: ({ context }) => loadAuthOptions(context.queryClient),
  component: LoginPage,
});

/** Why the user is here, in words (J-210, inbox 06 PR E point 3). */
function reasonMessage(
  reason: "idle" | "expired" | "locked" | "password_reset" | undefined,
  minutes: number | undefined,
) {
  // J-246: the reset page's return; every session of the account ended with the reset.
  if (reason === "password_reset") return "Your password was changed. Sign in with the new one.";
  if (reason === "locked") return "Your session is locked. Sign in to return to your page.";
  if (reason === "idle")
    return `You were signed out after ${minutes ?? DEFAULT_IDLE_MINUTES} minutes without activity.`;
  if (reason === "expired")
    return "Your session ended. Sign in again to return to the page you were on.";
  return null;
}

function LoginPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { redirect, reason, minutes } = Route.useSearch();
  const resetByEmail = Route.useLoaderData()?.passwordResetByEmail === true;
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const message = reasonMessage(reason, minutes);

  // J-243: every way a session ends arrives here, after the signed-in layout has left the screen, so
  // the previous user's cached reads and router state go now (`clear-user-state.ts` says why not earlier).
  useEffect(() => clearUserState(queryClient, router), [queryClient, router]);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setPending(true);
    const form = new FormData(e.currentTarget);
    const { error: err } = await authClient.signIn.email(
      { email: formString(form, "email"), password: formString(form, "password") },
      {
        onSuccess: (ctx) => {
          const data = ctx.data as { twoFactorRedirect?: boolean } | undefined;
          // J-210 (inbox 06 PR E point 4): the second step keeps the page to return to, and a target
          // that is not a path on this site (`//host`) lands on the default page. `href`, not `to`: the
          // page to return to carries its search (year, ACO, practice), which `to` would drop (J-209,
          // inbox 06 PR D item 17). J-243: the page is kept only for the user who left it; anyone else
          // lands on their own default page, with nothing cached from the previous user.
          if (data?.twoFactorRedirect) {
            void navigate({ to: "/two-factor", search: { redirect } });
            return;
          }
          const target = returnTarget(redirect, signedInUserId(ctx.data));
          clearUserState(queryClient, router);
          void navigate({ href: target, reloadDocument: isServerPage(target) });
        },
      },
    );
    setPending(false);
    if (err) {
      log.debug("sign-in rejected", err.status); // status only; never the email or password
      setError(
        err.status === 429
          ? "Too many sign-in attempts from this network. Wait a minute, then try again."
          : // J-408: the server says so only when the emailed temporary password was typed right.
            err.code === "TEMPORARY_PASSWORD_EXPIRED"
            ? "Your temporary password has expired. Ask your administrator for a new one."
            : "Email or password is incorrect.",
      );
    }
  }

  return (
    // A4, the focused-task archetype (03-FOUNDATIONS-LAYOUT-A11Y-CONTENT.md §2.6), in the owner's
    // approved design (J-242): one card, one primary action. Controls are 44 px or taller, the
    // comfortable size rather than the 32 px clinician compact size, because sign-in is reached from
    // any device including a phone at a bedside.
    <AuthPage
      heading="Sign in to VBLTC"
      description={
        <>
          <span className="block">Use your portal email and password.</span>
          <span className="block">You may be asked for an authenticator code next.</span>
        </>
      }
    >
      {/* One live region rather than an ad-hoc <p role="status">, so a later message cannot compete
          with this one for the same announcement. */}
      {message === null ? null : (
        <StatusRegion
          tone={reason === "password_reset" ? "success" : "moderate"}
          message={message}
          className="mb-4.5 rounded-md px-4"
        />
      )}
      <form
        onSubmit={(e) => void onSubmit(e)}
        aria-label="Sign in"
        className="flex flex-col gap-4.5"
      >
        {/* The label text is the Playwright locator and must not be renamed (see the file header). */}
        <label className={AUTH_LABEL_CLASS}>
          Email
          <Input
            name="email"
            type="email"
            autoComplete="username"
            required
            className={AUTH_INPUT_CLASS}
          />
        </label>
        <label className={AUTH_LABEL_CLASS}>
          Password
          <Input
            name="password"
            type="password"
            autoComplete="current-password"
            required
            minLength={15}
            className={AUTH_INPUT_CLASS}
          />
        </label>
        {error && (
          <p role="alert" className="text-destructive text-sm">
            {error}
          </p>
        )}
        <Button
          type="submit"
          disabled={pending}
          data-telemetry="login-submit"
          className={AUTH_BUTTON_CLASS}
        >
          Sign in
        </Button>
        {/* J-257: offered only where the server can mail the link (production has no transport). */}
        {resetByEmail ? (
          <Link to="/forgot-password" className={AUTH_LINK_CLASS} data-telemetry="login-forgot">
            Forgot password?
          </Link>
        ) : null}
      </form>
    </AuthPage>
  );
}
